import { Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule, DatePipe } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatChipsModule } from '@angular/material/chips'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatDialog } from '@angular/material/dialog'
import { filter, firstValueFrom } from 'rxjs'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { Meeting, AdminAgendaMetadata, GoverningBodyType, MeetingType } from '../meetings.service'
import { DatetimePickerComponent } from '../common/datetime-picker/datetime-picker.component'
import { SelectComponent, SelectOption } from '../common/select/select.component'
import {
  MAXIMUM_MEETING_DURATION_IN_MINUTES,
  MINIMUM_MEETING_DURATION_IN_MINUTES,
  MEETING_DURATION_INCREMENT_IN_MINUTES,
} from '../add-meeting-page/add-meeting-page.component'
import { isDateTooFarInPastForManualMeetingCreation } from '../utils'
import { ToastService } from '../toast.service'
import { MeetingEntitiesService } from '../meeting/meeting-entities/meeting-entities.service'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'

export type MeetingDetailsFormData = {
  date: Date | null
  duration: number
  location: string | null
  companyName: string | null
  governingBodyType: GoverningBodyType
  committeeType: string | null
  entities: string[]
  newEntity: string
  link: string | null
}

export type MeetingDetailsSaveData = {
  metadata: {
    location: string | null
    companyName: string | null
    governingBodyType: GoverningBodyType
    committeeType: string | null
    entities: string[]
  }
  dateTime: {
    date: Date
    endDate: Date
  } | null
  link: string | null
}

@Component({
  selector: 'app-meeting-details-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatTooltip,
    TranslatePipe,
    DatePipe,
    DatetimePickerComponent,
    SelectComponent,
    MatIconButtonSizesModule,
  ],
  templateUrl: './meeting-details-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-details-card.component.scss',
})
export class MeetingDetailsCardComponent {
  @Input({ required: true })
  meeting!: Meeting

  @Input({ required: true })
  metadata!: AdminAgendaMetadata

  @Input()
  canEdit: boolean = false

  @Input()
  title: string | null = null

  @Input()
  stepNumber: number | null = null

  @Input()
  description: string | null = null

  @Output()
  detailsSaved = new EventEmitter<MeetingDetailsSaveData>()

  isEditingDetails = false
  editedDetailsFormData: MeetingDetailsFormData = {
    date: null,
    duration: 0,
    location: null,
    companyName: null,
    governingBodyType: GoverningBodyType.OTHER,
    committeeType: null,
    entities: [],
    newEntity: '',
    link: null,
  }

  governingBodyTypeOptions: SelectOption<GoverningBodyType>[] = Object.values(GoverningBodyType).map((type) => ({
    value: type,
    labelKey: `MEETING_SETUP.MEETING_DETAILS.GOVERNING_BODY_TYPE.${type}`,
  }))

  get canEditDateTime(): boolean {
    return this.meeting.isUpcoming && this.meeting.type !== 'SCHEDULED'
  }

  get increaseDurationButtonDisabled(): boolean {
    return this.editedDetailsFormData.duration >= MAXIMUM_MEETING_DURATION_IN_MINUTES || !this.canEditDateTime
  }

  get decreaseDurationButtonDisabled(): boolean {
    return this.editedDetailsFormData.duration <= MINIMUM_MEETING_DURATION_IN_MINUTES || !this.canEditDateTime
  }

  get isUploadedMeeting(): boolean {
    return this.meeting.type === MeetingType.AUDIO_UPLOAD || this.meeting.type === MeetingType.VIDEO_UPLOAD
  }

  get isLiveMeeting(): boolean {
    return this.meeting.type === MeetingType.LIVE
  }

  get canEditLink(): boolean {
    return this.meeting.isUpcoming
  }

  isDateTooFarInPast(date: Date | null): boolean {
    if (!this.isLiveMeeting) {
      return false
    }
    return isDateTooFarInPastForManualMeetingCreation(date)
  }

  get isLinkValid(): boolean {
    if (this.isUploadedMeeting || !this.canEditLink) {
      return true
    }
    const link = this.editedDetailsFormData.link?.trim() ?? ''
    return link !== ''
  }

  get isFormValid(): boolean {
    return this.isLinkValid && !this.isDateTooFarInPast(this.editedDetailsFormData.date)
  }

  startEditDetails() {
    const startDate = new Date(this.meeting.date)
    const endDate = new Date(this.meeting.endDate)
    const durationInMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60))

    this.editedDetailsFormData = {
      date: startDate,
      duration: durationInMinutes,
      location: this.metadata.location,
      companyName: this.metadata.companyName,
      governingBodyType: this.metadata.governingBodyType ?? GoverningBodyType.OTHER,
      committeeType: this.metadata.committeeType,
      entities: [...this.metadata.entities],
      newEntity: '',
      link: this.meeting.link,
    }
    this.isEditingDetails = true
  }

  cancelEditDetails() {
    this.isEditingDetails = false
    this.editedDetailsFormData = {
      date: null,
      duration: 0,
      location: null,
      companyName: null,
      governingBodyType: GoverningBodyType.OTHER,
      committeeType: null,
      entities: [],
      newEntity: '',
      link: null,
    }
  }

  saveDetails() {
    const saveData: MeetingDetailsSaveData = {
      metadata: {
        location: this.editedDetailsFormData.location,
        companyName: this.editedDetailsFormData.companyName,
        governingBodyType: this.editedDetailsFormData.governingBodyType,
        committeeType:
          this.editedDetailsFormData.governingBodyType === GoverningBodyType.COMMITTEE ? this.editedDetailsFormData.committeeType?.trim() || null : null,
        entities: this.editedDetailsFormData.entities,
      },
      dateTime: null,
      link: this.canEditLink ? this.editedDetailsFormData.link : this.meeting.link,
    }

    if (this.editedDetailsFormData.date !== null) {
      const originalDate = new Date(this.meeting.date)
      const originalEndDate = new Date(this.meeting.endDate)
      const originalDuration = Math.round((originalEndDate.getTime() - originalDate.getTime()) / (1000 * 60))

      const dateChanged = this.editedDetailsFormData.date.getTime() !== originalDate.getTime()
      const durationChanged = this.editedDetailsFormData.duration !== originalDuration

      if (dateChanged || durationChanged) {
        const newEndDate = new Date(this.editedDetailsFormData.date.getTime() + this.editedDetailsFormData.duration * 60 * 1000)
        saveData.dateTime = {
          date: this.editedDetailsFormData.date,
          endDate: newEndDate,
        }
      }
    }

    this.detailsSaved.emit(saveData)
    this.isEditingDetails = false
  }

  increaseDuration() {
    if (this.editedDetailsFormData.duration < MAXIMUM_MEETING_DURATION_IN_MINUTES) {
      this.editedDetailsFormData.duration += MEETING_DURATION_INCREMENT_IN_MINUTES
    }
  }

  decreaseDuration() {
    if (this.editedDetailsFormData.duration > MINIMUM_MEETING_DURATION_IN_MINUTES) {
      this.editedDetailsFormData.duration -= MEETING_DURATION_INCREMENT_IN_MINUTES
    }
  }

  addEntity() {
    const trimmedEntity = this.editedDetailsFormData.newEntity.trim()
    if (trimmedEntity !== '' && !this.editedDetailsFormData.entities.includes(trimmedEntity)) {
      this.editedDetailsFormData.entities = [...this.editedDetailsFormData.entities, trimmedEntity]
      this.editedDetailsFormData.newEntity = ''
    }
  }

  async removeEntity(entity: string) {
    if (this.meeting.allowMeetingEntities && !(await this.confirmRemovingEntityWithWork(entity))) {
      return
    }
    this.editedDetailsFormData.entities = this.editedDetailsFormData.entities.filter((item) => item !== entity)
  }

  /** Saving the details deletes an entity that is no longer named, and with it the time marks and minutes it has. */
  private async confirmRemovingEntityWithWork(name: string): Promise<boolean> {
    const result = await firstValueFrom(
      this.meetingEntitiesService.getOverview(this.meeting.id).pipe(filter((candidate) => candidate.data !== undefined || candidate.isError))
    )
    const overview = result.data ?? null
    // Without the entities nothing says what the removal would delete, so it waits until they can be read.
    if (overview === null) {
      this.toastService.show('MEETING_ENTITIES.UPDATE_FAILED')
      return false
    }
    const entity = overview.entities.find((candidate) => candidate.name.trim().toLocaleLowerCase() === name.trim().toLocaleLowerCase()) ?? null
    if (entity === null || (entity.intervals.length === 0 && !entity.hasMinutes && entity.minutesStatus === 'NOT_GENERATED')) {
      return true
    }
    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: this.translateService.instant('MEETING_ENTITIES.DELETE_DIALOG_TITLE', { name: entity.name }),
            description: this.translateService.instant('MEETING_ENTITIES.DELETE_DIALOG_DESCRIPTION'),
          },
        })
        .afterClosed()
    )
    return confirmed === true
  }

  private toastService = inject(ToastService)
  private meetingEntitiesService = inject(MeetingEntitiesService)
  private dialog = inject(MatDialog)
  private translateService = inject(TranslateService)

  copyLink() {
    if (this.meeting.link === null) {
      return
    }
    navigator.clipboard.writeText(this.meeting.link)
    this.toastService.show('COMMON.COPIED_TO_CLIPBOARD')
  }
}
